import React, { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLands } from "../../../shared/hooks/useLands";
import InfiniteScroll from "react-infinite-scroll-component";
import generateMeta from "../../../shared/services/metadataService";
import metaData from "../../../config/metaData";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { useTranslation } from "react-i18next";
import LandItem from "../../../components/landItem";
import FixedDropdown from "../../../components/FixedDropdown";
import { fadeIn, staggerContainer, itemAnimation } from "./animations";
import { LandsSkeleton } from "./components/LandsSkeleton";
import { ErrorMessage, NoResults } from "./components/ErrorAndNoResults";
import Logo from "../../../assets/images/logo.svg";

const Lands = () => {
  const { t } = useTranslation();
  const [page, setPage] = useState<number>(1);
  const { lands, totalItems, perPage, loading, error, hasMore, fetchLands } =
    useLands(page);

  const [selectedSort, setSelectedSort] = useState<string>("");
  const sortOptions = useSelector(
    (state: RootState) => state.config.landSortOptions,
  );

  // Apply client-side filtering and sorting
  const filteredAndSortedLands = useMemo(() => {
    let result = [...lands];

    // Apply sorting
    if (selectedSort) {
      const sortOption = sortOptions.find(
        (option) => option.value === selectedSort,
      );
      if (sortOption) {
        const { sort_by, sort_order } = sortOption;

        if (sort_by === "price") {
          result.sort((a, b) => {
            const priceA = parseFloat(a.price);
            const priceB = parseFloat(b.price);
            return sort_order === "asc" ? priceA - priceB : priceB - priceA;
          });
        }
      }
    }

    return result;
  }, [lands, selectedSort, sortOptions]);

  const loadMore = () => {
    if (!loading && hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchLands(nextPage);
    }
  };

  const handleSortChange = (value: string) => {
    setSelectedSort(value);
  };

  const hasResults = filteredAndSortedLands.length > 0;

  // Count of active filters
  const activeFiltersCount = [selectedSort].filter(Boolean).length;

  // Convert sort options to dropdown format
  const sortDropdownOptions = useMemo(() => {
    const defaultOption = {
      id: "default",
      label: t("lands.filters.default"),
      value: "",
    };

    const options = sortOptions.map((option) => ({
      id: option.id,
      label: option.label,
      value: option.value,
    }));

    return [defaultOption, ...options];
  }, [sortOptions, t]);

  // Render metadata
  const renderMeta = () =>
    generateMeta({
      title: metaData.lands.title,
      description: metaData.lands.description,
      keywords: metaData.lands.keywords?.split(", "),
      slug: "land-for-sale",
    });

  // Render skeleton for initial loading
  if (loading && lands.length === 0) {
    return <LandsSkeleton />;
  }

  return (
    <div className="min-h-screen bg-white pt-[20px]">
      {renderMeta()}

      {/* Main content */}
      <motion.div
        className="py-24 px-[6%] max-w-screen-2xl mx-auto"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header */}
        <motion.div
          className="mb-16"
          variants={fadeIn}
          initial="initial"
          animate="animate"
        >
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-6">
            <div>
              <h5 className="text-sm tracking-wider uppercase text-gray-500 mb-3">
                {t("lands.pageSubtitle")}
              </h5>
              <h1 className="text-4xl md:text-5xl font-title italic font-medium">
                {t("lands.pageTitle")}
              </h1>
            </div>
            {activeFiltersCount > 0 && (
              <div className="flex items-center">
                <span className="mr-2 text-sm">
                  {activeFiltersCount === 1
                    ? t("lands.filters.activeFilters", {
                        count: activeFiltersCount,
                      })
                    : t("lands.filters.activeFiltersPlural", {
                        count: activeFiltersCount,
                      })}
                </span>
                <button
                  className="text-sm flex items-center hover:underline"
                  onClick={() => {
                    setSelectedSort("");
                  }}
                >
                  {t("lands.filters.clearAll")}
                  <svg
                    className="w-3 h-3 ml-1"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            )}
          </div>
          {/* Filter controls */}
          <div className="border-t border-b border-gray-200 py-6">
            <div className="relative mt-2 w-full md:w-[40%] md:ml-[30%]">
              <img
                className="absolute top-[2%] left-[2%] w-[26%] invert brightness-0"
                src={Logo}
                alt="logo"
              />
              <img
                className="w-full object-contain rounded-md"
                src={require("../../../assets/images/lots.webp")}
                width="auto"
                height="auto"
                alt="land"
              />
            </div>
            <div className="border-solid border-b border-gray-200 my-[3%]"></div>
            <div className="flex flex-wrap gap-6">
              <FixedDropdown
                label={t("lands.filters.sortBy")}
                options={sortDropdownOptions}
                value={selectedSort}
                onChange={handleSortChange}
                placeholder={t("lands.filters.default")}
                className="w-full md:w-auto min-w-[200px]"
              />
            </div>
          </div>
        </motion.div>

        {/* Error message - only show if we have no data */}
        {error && lands.length === 0 && (
          <ErrorMessage
            error={error}
            onRetry={() => {
              setPage(1);
              fetchLands(1);
            }}
          />
        )}

        {/* No results state */}
        {!hasResults && !error && (
          <NoResults
            onResetFilters={() => {
              setSelectedSort("");
            }}
          />
        )}

        {/* Results grid with infinite scroll */}
        {hasResults && (
          <InfiniteScroll
            dataLength={filteredAndSortedLands.length}
            next={loadMore}
            hasMore={hasMore}
            loader={
              <div className="w-full flex justify-center py-12" key="loader">
                <div className="flex flex-col items-center">
                  <div className="w-8 h-8 border-t-2 border-b-2 border-black rounded-full animate-spin mb-4"></div>
                  <p className="text-sm text-gray-500">
                    {t("lands.loading.message")}
                  </p>
                </div>
              </div>
            }
            endMessage={
              <div className="text-center py-8 text-gray-500">
                {t("lands.results.endMessage")}
              </div>
            }
            scrollThreshold={0.7}
          >
            <motion.div
              className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-x-8 gap-y-12"
              variants={staggerContainer}
              initial="initial"
              animate="animate"
            >
              <AnimatePresence mode="popLayout">
                {filteredAndSortedLands.map((land) => (
                  <motion.div
                    key={land.id}
                    layoutId={`land-${land.id}`}
                    variants={itemAnimation}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                  >
                    <LandItem
                      id={land.id}
                      title={land.title}
                      price={parseFloat(land.price)}
                      imageUrl={land.main_image_url}
                      area={parseFloat(land.area.value)}
                    />
                  </motion.div>
                ))}
              </AnimatePresence>
            </motion.div>
          </InfiniteScroll>
        )}

        {/* Results summary */}
        {hasResults && (
          <div className="mt-16 pt-8 border-t border-gray-200 text-gray-500 text-sm">
            {t("lands.results.summary", {
              shown: filteredAndSortedLands.length,
              total: totalItems,
            })}
          </div>
        )}

        {/* Error message for subsequent loads */}
        {error && lands.length > 0 && (
          <div className="mt-8 p-4 bg-red-50 border border-red-200 text-red-600 rounded">
            <div className="flex items-center">
              <svg
                className="w-5 h-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <p>{error}</p>
            </div>
            <button
              className="mt-2 text-sm underline"
              onClick={() => {
                setPage(page);
                fetchLands(page);
              }}
            >
              {t("lands.error.tryAgain")}
            </button>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default Lands;
